/** Groups render in this order, whatever order items were registered in. */
export const GROUP_ORDER = [
  "recent",
  "actions",
  "event",
  "proposals",
  "source",
  "registration",
  "org",
  "settings",
  "reporting",
  "events",
  "help",
] as const;

/** Section headers shown above each group. Sentence case, per the style guide. */
export const GROUP_LABELS: Record<(typeof GROUP_ORDER)[number], string> = {
  recent: "Recent",
  actions: "Actions",
  event: "This event",
  // Venue names from the event's own proposals, fetched while you type. Sits
  // above the static destinations because a hotel name is a specific thing the
  // user is looking for, and "Proposals" under Source is the general one.
  proposals: "Proposals",
  // "Source" and "Registration" mirror the sidebar's own section titles, so the
  // palette's vocabulary matches the one the user already learned from the nav.
  source: "Source",
  registration: "Registration",
  org: "Organization",
  settings: "Settings",
  reporting: "Reporting",
  events: "Events",
  help: "Help",
};

/** Placeholder on the root page. */
export const ROOT_PLACEHOLDER = "Run a command or search...";

/** Trailing marker on labels that open a nested page rather than navigating. */
export const PAGE_SUFFIX = "...";

/** Where the zero-result handoff sends the user. */
export const CHAT_PATH = "/dashboard/chat";

/** Copy for the zero-result state, which is a door rather than a dead end. */
export const EMPTY_TITLE = "No results";
export const EMPTY_HELP = "How can we help?";

/**
 * The dialog's stacking layer.
 *
 * The palette is a global surface, so it has to clear the overlays a route can
 * already have open, or Cmd+K mounts an invisible dialog that still holds a
 * focus trap. Real layers it clears, none of them written as a literal:
 * `GenericModal`'s default 99999, and `event-details-agenda`'s planner at
 * `EDIT_MODAL_Z_INDEX + 2` (99995) with its session popover at +4 (99997).
 *
 * Deliberately below the toast viewport (`z-[100001]`), so a toast the palette
 * itself raises stays readable. That band also holds a handful of dashboard
 * overlays (`FetchTooltip`, `SendBlastModal`, `ManageAccessPanel`) and
 * `PICKER_POPUP_Z_INDEX` sits above at 100050, so the palette can still open
 * beneath one of those. Escape is handled: the dialog registers on the shared
 * `modalStack` while open, so only it responds. Ordering the whole app's
 * overlay band is its own change, not this one.
 *
 * Shared by both mounts, so this moves dashboard stacking too.
 *
 * Tailwind extracts class names statically, so `CommandPalette` has to write the
 * literal `z-[100000]` rather than interpolate this. A test asserts they agree.
 */
export const PALETTE_Z_INDEX = 100000;

/**
 * Why a row is disabled, shown in its trailing slot and folded into its
 * accessible name. Named rather than inlined because each is asserted in tests
 * and read by more than one item.
 */
export const DISABLED_REASON = {
  /** For rows gated on the surface itself. */
  dashboardOnly: "Dashboard only",
  /** For rows gated on a capability that may be absent anywhere. */
  unavailableHere: "Unavailable here",
  /** For a row that would navigate to where the user already is. */
  alreadyHere: "Already here",
} as const;

/**
 * Ceiling on the destinations posted to the AI chat with a message (ENG-836).
 *
 * Must stay at or below ZED's own `MAX_DESTINATIONS` in
 * `apps/zed/src/threads/constants.py`, which keeps the first N and drops the
 * rest. The registry is 36 rows today, so this is roughly double: it exists to
 * make an unbounded registry visible here rather than silently truncated there.
 */
export const MAX_CHAT_DESTINATIONS = 80;

/**
 * Ceiling on the synonyms sent per destination.
 *
 * Mirrors ZED's `MAX_KEYWORDS_PER_DESTINATION`, which truncates past it. The
 * fattest row today is "Contract clauses" at 14, and `keywords.ts` is edited as
 * the zero-result log is read, so this is the number that actually moves.
 */
export const MAX_CHAT_DESTINATION_KEYWORDS = 24;
